import { useState } from "react"
import { ButtonGroup, HStack, IconButton, Link as TriggerLink, Pagination, Text } from "@chakra-ui/react"
import { Tooltip } from "@hydrologiq/shared/chakra/Tooltip"
import { HiChevronLeft, HiChevronRight } from "react-icons/hi"
import Figure from "./Figure"
import CurrentOrderDialog from "@hydrologiq/shared/components/CurrentOrderDialog"
import * as gqlTypes from "@hydrologiq/gql-experience/graphql"
import FigureContent from "@hydrologiq/shared/figures/generic/FigureContent"
import { useCopyValues } from "@hydrologiq/shared/providers/helpers/useCopy"
import { COPY_KEYS, DEFAULT_COPY_MAPPINGS } from "@hydrologiq/shared/copy/constants"
import Link from "@hydrologiq/shared/components/Link"
import { isCompactFigure } from "@hydrologiq/shared/figures/generic/figureVariant"

export interface CurrentOrderData {
  id: string
  name: string
  status: gqlTypes.OrderStatus
  start: string
  end: string
  quantity?: number | null
  quantity_consumed?: number | null
  customer?: { id: string; name: string } | null
  site?: { id: string; name: string } | null
  sites?: Array<{
    customer: { id: string; name: string }
    site: { id: string; name: string }
  }>
  assets?: Array<{
    id: string
    name: string
    kind: string
    status: string
    category: string
    class: string
    volume?: number | null
  }>
}

interface CurrentOrderStatusProps {
  orders: CurrentOrderData[]
  assetId?: string
  customerId?: string
  loading?: boolean
  showSite?: boolean
}

const CurrentOrderStatus = ({
  orders,
  assetId,
  customerId,
  loading = false,
  showSite = false,
}: CurrentOrderStatusProps) => {
  const [currentPage, setCurrentPage] = useState(1)
  const [dialogOpen, setDialogOpen] = useState(false)

  const copy = useCopyValues({
    [COPY_KEYS.HEADINGS.ORDER]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.HEADINGS.ORDER],
    [COPY_KEYS.HEADINGS.ORDERS]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.HEADINGS.ORDERS],
  })

  // Helper function to extract and format customer site name from order data
  const getCustomerSiteName = (order: CurrentOrderData): string => {
    if (!showSite) {
      return "current"
    }
    // Use the new sites array format as specified in the plan
    let firstSite: { customer: { name: string }; site: { name: string } } | undefined
    if (order.sites) {
      firstSite = order.sites[0]
    } else if (order.site) {
      firstSite = { customer: { name: order.customer?.name || "" }, site: { name: order.site?.name || "" } }
    }

    if (firstSite) {
      const customerName = firstSite.customer?.name
      const siteName = firstSite.site?.name

      // If we're in customer context, only show site name
      if (customerId && siteName) {
        return siteName
      }

      // If not in customer context, show full "Customer - Site" format
      if (customerName && siteName) {
        return `${customerName} - ${siteName}`
      }
      if (customerName) {
        return customerName
      }
      if (siteName) {
        return siteName
      }
    }

    // Fallback to "Current" when no site data is available
    return "current"
  }

  // Handle single order or paginated orders
  const currentOrder = orders.length > 0 ? orders[currentPage - 1] : null
  const hasMultipleOrders = orders.length > 1

  const getAllOrdersLink = () => {
    if (assetId) {
      return `/orders?asset=${assetId}`
    }
    if (customerId) {
      return `/orders?customer=${customerId}`
    }
    return "/orders"
  }

  const renderContent = () => {
    if (loading) {
      return <Text>Loading...</Text>
    }

    if (!currentOrder) {
      return (
        <FigureContent.Root>
          <FigureContent.SubText link={<Link href={getAllOrdersLink()} variant={"primary" as any} fontSize="sm">
              All {copy[COPY_KEYS.HEADINGS.ORDERS].toLowerCase()}
            </Link>}>current</FigureContent.SubText>
          <FigureContent.Value>No {copy[COPY_KEYS.HEADINGS.ORDER].toLowerCase()}</FigureContent.Value>
        </FigureContent.Root>
      )
    }

    return (
      <FigureContent.Root>
        <FigureContent.SubText link={<Link href={getAllOrdersLink()} variant={"primary" as any} fontSize="sm">
            All {copy[COPY_KEYS.HEADINGS.ORDERS].toLowerCase()}
          </Link>}>
            current
        </FigureContent.SubText>
        <HStack width="100%" justifyContent="space-between" alignItems="center" flexWrap="nowrap">
          <FigureContent.Value fontSize="md" truncate maxWidth={hasMultipleOrders ? "60%" : "100%"} tooltip={currentOrder.name}>
            <TriggerLink
              variant={"primary" as any}
              onClick={() => setDialogOpen(true)}
              aria-label={`View details for ${copy[COPY_KEYS.HEADINGS.ORDER].toLowerCase()} ${currentOrder.name}`}
              truncate
              maxWidth="100%"
            >
              {currentOrder.name}
            </TriggerLink>
          </FigureContent.Value>
          {hasMultipleOrders && (
            <Pagination.Root
              count={orders.length}
              pageSize={1}
              page={currentPage}
              onPageChange={(e) => setCurrentPage(e.page)}
            >
              <ButtonGroup variant="ghost" size="xs" gap="1" flexShrink={0}>
                <Pagination.PrevTrigger asChild>
                  <IconButton size="xs">
                    <HiChevronLeft />
                  </IconButton>
                </Pagination.PrevTrigger>
                <Pagination.PageText whiteSpace="nowrap" />
                <Pagination.NextTrigger asChild>
                  <IconButton size="xs">
                    <HiChevronRight />
                  </IconButton>
                </Pagination.NextTrigger>
              </ButtonGroup>
            </Pagination.Root>
          )}
        </HStack>
      </FigureContent.Root>
    )
  }

  return (
    <>
      <Figure heading={`${copy[COPY_KEYS.HEADINGS.ORDER]}s`} loading={loading} compact={isCompactFigure("summary")}>
        {renderContent()}
      </Figure>

      {/* Order details dialog */}
      {currentOrder && (
        <CurrentOrderDialog
          order={currentOrder}
          assetId={assetId}
          customerId={customerId}
          open={dialogOpen}
          onOpenChange={setDialogOpen}
        />
      )}
    </>
  )
}

export default CurrentOrderStatus
